import { DateTime } from 'luxon';
import {
    FileParameter,
    IPlaylistAdminDto,
    IPlaylistDataDto,
    IPlaylistImportResultDto,
    IStoredFileDto,
    PlaylistCatalogControllerClient,
    PlaylistPublicControllerClient,
    UpsertPlaylistDto,
    UpsertPlaylistItemDto,
    Order,
    SortBy,
} from '../clients/services';
import clientFactory from './client-factory';
import { FileLibraryService } from './file-library.service';
import { ReservationService } from './reservation.service';

const serviceUrl = (process.env.REACT_APP_SERVICE_URL as string | undefined)?.replace(/\/$/, '') ?? '';

export interface IPlaylistSuggestionData {
    artists: string[];
    albumsByArtist: Map<string, string[]>;
    albums: string[];
}

export class PlaylistAdminService {
    private readonly playlistCatalogClient: PlaylistCatalogControllerClient;
    private readonly playlistPublicClient: PlaylistPublicControllerClient;
    private readonly fileLibraryService: FileLibraryService;
    private readonly reservationService: ReservationService;

    constructor() {
        this.playlistCatalogClient = clientFactory.createSecuredClient(PlaylistCatalogControllerClient);
        this.playlistPublicClient = clientFactory.createClient(PlaylistPublicControllerClient);
        this.fileLibraryService = new FileLibraryService();
        this.reservationService = new ReservationService();
    }

    async loadAdminPlaylistsAsync(): Promise<IPlaylistAdminDto[]> {
        const playlists = await this.playlistCatalogClient.getPlaylists();
        return playlists.sort((a, b) => this.getSortScore(b.year, b.month) - this.getSortScore(a.year, a.month));
    }

    async createPlaylistAsync(year: number, month?: number): Promise<IPlaylistAdminDto> {
        const payload = new UpsertPlaylistDto({
            year,
            month: month ?? null,
            playlist: [],
        });

        return await this.playlistCatalogClient.createPlaylist(payload);
    }

    async updatePlaylistAsync(playlist: IPlaylistAdminDto): Promise<IPlaylistAdminDto> {
        const payload = new UpsertPlaylistDto({
            year: playlist.year,
            month: playlist.month ?? null,
            image: playlist.image ?? null,
            playlist: (playlist.playlist ?? []).map(
                (entry) =>
                    new UpsertPlaylistItemDto({
                        artist: (entry.artist ?? '').trim(),
                        title: (entry.title ?? '').trim(),
                    }),
            ),
        });

        return await this.playlistCatalogClient.updatePlaylist(playlist.id, payload);
    }

    async deletePlaylistAsync(id: number): Promise<void> {
        await this.playlistCatalogClient.deletePlaylist(id);
    }

    async importPlaylistAsync(file: File, dryRun: boolean): Promise<IPlaylistImportResultDto> {
        const fileParameter: FileParameter = {
            data: file,
            fileName: file.name,
        };

        return await this.playlistCatalogClient.importPlaylist(fileParameter, dryRun);
    }

    async listMediaImagesAsync(): Promise<IStoredFileDto[]> {
        return await this.fileLibraryService.listFilesAsync('', SortBy.CreatedAt, Order.Desc, '');
    }

    async uploadImageAsync(file: File): Promise<string> {
        const uploaded = await this.fileLibraryService.uploadFileAsync(file, file.name, '', 'images');
        return this.resolvePublicUrl(uploaded.publicUrl ?? '');
    }

    resolvePublicUrl(rawUrl: string): string {
        if (!rawUrl) {
            return '';
        }

        if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) {
            return rawUrl;
        }

        if (rawUrl.startsWith('/images/') || rawUrl.startsWith('images/')) {
            return rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`;
        }

        const normalizedPath = rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`;
        if (!serviceUrl) {
            return normalizedPath;
        }

        return `${serviceUrl}${normalizedPath}`;
    }

    async loadSuggestionDataAsync(playlists?: IPlaylistAdminDto[]): Promise<IPlaylistSuggestionData> {
        const sourcePlaylists = playlists ?? (await this.loadAdminPlaylistsAsync());
        const artistSet = new Set<string>();
        const albumSet = new Set<string>();
        const albumByArtist = new Map<string, Set<string>>();

        sourcePlaylists.forEach((playlist) => {
            (playlist.playlist ?? []).forEach((entry) => {
                const artist = (entry.artist ?? '').trim();
                const album = (entry.title ?? '').trim();

                if (artist) {
                    artistSet.add(artist);
                }

                if (album) {
                    albumSet.add(album);
                }

                if (artist && album) {
                    const key = artist.toLowerCase();
                    if (!albumByArtist.has(key)) {
                        albumByArtist.set(key, new Set<string>());
                    }
                    albumByArtist.get(key)?.add(album);
                }
            });
        });

        try {
            const concertSeries = await this.reservationService.getConcertSeries();
            concertSeries.forEach((series) => {
                (series.artists ?? []).forEach((artist) => {
                    const name = (artist.name ?? '').trim();
                    if (name) {
                        artistSet.add(name);
                    }
                });
            });
        } catch {
            // Suggestions still work from playlist data if concert series lookup fails.
        }

        const albumsByArtist = new Map<string, string[]>();
        albumByArtist.forEach((value, key) => {
            albumsByArtist.set(
                key,
                Array.from(value).sort((a, b) => a.localeCompare(b, 'de-CH')),
            );
        });

        return {
            artists: Array.from(artistSet).sort((a, b) => a.localeCompare(b, 'de-CH')),
            albumsByArtist,
            albums: Array.from(albumSet).sort((a, b) => a.localeCompare(b, 'de-CH')),
        };
    }

    async loadNewestPlaylistAsync(): Promise<IPlaylistDataDto> {
        return await this.playlistPublicClient.getNewestPlaylist();
    }

    async loadNewestThreePlaylistsAsync(): Promise<IPlaylistDataDto[]> {
        return await this.playlistPublicClient.getNewestThreePlaylists();
    }

    private getSortScore(year?: number | null, month?: number | null): number {
        const safeYear = year ?? DateTime.now().year;
        const safeMonth = month ?? 0;
        return safeYear * 13 + safeMonth;
    }
}
